// How bad a disruption is — the judgement the whole app rests on.
//
// PURE, AND WITH ONE IMPORT: `./modes.js`, which imports nothing itself. Not
// Vue, not the store, not the browser.
// That is not tidiness: this file is imported by `server/push.mjs`, which runs
// in Node on Cloud Run and decides whether to wake somebody's phone. The phone
// and the server MUST agree about what counts as a delay, and the only way to
// guarantee that is for both to run the same code. A second copy of these
// regular expressions living server-side would drift within a month, and the
// symptom would be a notification for a car park closure.
//
// `src/app-state.js` re-exports everything here, so nothing else in the app
// changed when it moved.

import { capital, everyAdapterMode, modeOfRow, vehicleWords } from './modes.js';

const vehicleModes = () => everyAdapterMode().filter((m) => m.severity.rules === 'street');

/**
 * How late, in minutes, if the notice says so.
 *
 * PTV's disruptions endpoint has no delay field — but its prose almost always
 * carries the figure: "delays of up to 20 minutes are expected", "running up to
 * 8 minutes late". Reading it out is the difference between the row showing the
 * word DELAYED and showing `+20` over `MIN`, which is the state the ledger was
 * designed around.
 *
 * Deliberately narrow. It matches a number immediately qualified as a delay and
 * nothing else, so "buses depart every 20 minutes" and "8 minutes to Flinders
 * Street" do not become delay figures. When in doubt it returns null and the
 * row falls back to the word, which is never wrong — only less useful.
 */
const DELAY_FIGURE = /(?:up to|about|around|approximately|by)\s+(\d{1,3})\s*(?:-|to)?\s*(?:\d{1,3})?\s*min(?:ute)?s?\b(?![^.]*\bapart\b)|(\d{1,3})\s*min(?:ute)?s?\s+(?:late|delay)/i;

// Street modes (trams) also say "delayed up to 1 hour" and "half an hour".
// Opt-in via `{ hours: true }` so a train notice with those words is read
// exactly as it always was.
const DELAY_HOURS = /(?:delay(?:s|ed)?\b[^.]{0,40}?|up to|about|around|approximately|by)\s+(?:(half an hour)|(?:an?|(\d{1,2}))\s*(?:-|to)?\s*hours?\b)/i;

export function delayMinutesFrom(text, { hours = false } = {}) {
  const t = String(text ?? '');
  const m = DELAY_FIGURE.exec(t);
  if (!m) {
    if (!hours) return null;
    const h = DELAY_HOURS.exec(t);
    if (!h) return null;
    const n = h[1] ? 30 : (h[2] ? Number(h[2]) : 1) * 60;
    return n > 0 && n <= 180 ? n : null;
  }
  const n = Number(m[1] ?? m[2]);
  // A three-hour "delay" is a suspension described badly, and a zero is noise.
  return Number.isFinite(n) && n > 0 && n <= 180 ? n : null;
}

// How severe a disruption is — the most consequential judgement in the app, and
// the one it got wrong in the most expensive direction. Every notice that was
// not "planned" read as a delay, so on an ordinary Wednesday morning fifteen of
// seventeen lines said DELAYED because of car park closures, station night
// works and pedestrian detours. A ledger that is always red is a ledger nobody
// reads, and an app nobody believes.
//
// PTV's own `disruption_type` is the authority — "Minor Delays", "Cancellation",
// "Planned Works", "Station detour" — and the prose is consulted only for what
// the type leaves open. Anything unrecognised is a NOTICE: it still shows on the
// line's detail screen, it just does not colour the ledger or buzz a phone.

/** PTV types that mean the trains themselves are late. */
const DELAY_TYPES = new Set(['Minor Delays', 'Major Delays']);

/** Planned types that describe the LINE running, rather than a station's fittings. */
const PLANNED_TYPES = new Set(['Planned Works', 'Planned Closure', 'Power outage', 'Diversion']);

/**
 * No train is coming at all — the one state that earns the signal colour.
 *
 * `coaches?` AS WELL AS `buses?`, and `will` as well as `are`, because V/Line
 * does not say "buses". Every regional replacement notice in the live feed is
 * worded "Coaches replace evening trains on the Bendigo Line" or "Coaches will
 * replace trains for the entire journey between Southern Cross and Albury" —
 * the same fact as a metropolitan bus replacement, in the operator's own word
 * for the vehicle. Without the alternative, a V/Line line with no train on it
 * all day read as a NOTICE: not on the ledger, not worth a push, visible only
 * to somebody who opened the line's own screen.
 *
 * The metropolitan feed never says "coaches", so nothing about it changes.
 */
// `(\s+\w+){0,2}` between the verb and "trains" because V/Line says WHICH
// trains: "Coaches replace evening trains on the Bendigo Line", "Coaches
// replace all trains". Two words is enough for every phrasing in the live feed
// and short enough that it cannot reach across a clause into an unrelated
// sentence.
/**
 * Exported because the reroute engine asks the same question of a row that
 * `severityOf` does, for a different reason: a suspension whose prose says
 * BUSES is a suspension with a replacement service on it, and that is a
 * segment the planner can offer rather than a hole it has to walk round.
 */
// The vehicle words of every street-running mode beside `trains` (Melbourne's
// notices say "Buses replace trams between Stop 3 and Stop 7"), read from the
// mode registry. No train notice says either.
//
// THE OTHER OPERATORS SAY IT DIFFERENTLY, and each phrasing below is one that is
// in a live feed. Adelaide Metro: "substitute buses will replace train
// services", "the full Outer Harbor rail line will be closed". Transport for
// NSW: "Inner West trains do not run", and on NSW TrainLink "this service has
// been replaced by road coaches for the entire journey" or "will terminate at
// Casino. Passengers will board awaiting coaches". Translink: "trains will not
// run between Northgate and Shorncliffe" under a "Weekday track closure"
// headline. The words that are NOT here are as deliberate as the ones that are:
// "no substitute bus services will operate" (Adelaide, a PARTIAL tram closure
// where trams keep running) and "temporary stop closure" (a bus stop) both
// describe something other than a missing train, so neither "operate" on its
// own nor a bare "temporarily closed" is a rule (Translink: "Moorooka station
// temporarily closed" is a station the trains run through).
const BUS_LIKE = String.raw`(?:(?:substitute|replacement|rail replacement|road)\s+)?(?:bus(?:es)?|coach(?:es)?|railbus(?:es)?|minibus(?:es)?)(?:\s+services?)?`;
// Only the modes a replacement bus can stand in for: see `vehicleWords`.
const ROAD_WORDS = vehicleWords({ replaceable: true }).join('|');
const TRAIN_LIKE = String.raw`(?:trains?${ROAD_WORDS ? `|${ROAD_WORDS}` : ''})(?:\s+services?)?`;
export const NOT_RUNNING = new RegExp(
  String.raw`\b(?:`
  + [
    // Buses replace trains / substitute buses will replace train services.
    String.raw`${BUS_LIKE}\s+(?:(?:are|will)\s+)?(?:be\s+)?replac(?:e|es|ing)(?:\s+\w+){0,2}\s+${TRAIN_LIKE}`,
    // The service has been replaced by road coaches.
    String.raw`replaced\s+(?:by|with)\s+(?:an?\s+|the\s+|additional\s+)?${BUS_LIKE}`,
    // Passengers will board awaiting coaches.
    String.raw`board\s+(?:the\s+)?(?:awaiting|waiting|replacement)\s+${BUS_LIKE}`,
    // Rail replacement buses, replacement bus services.
    String.raw`(?:rail\s+)?replacement\s+(?:bus(?:es)?|coach(?:es)?|services?)`,
    // Trains do not run. TRAINS OR A ROAD MODE'S VEHICLE ONLY, and not "services", and not
    // "will not operate" alone: "the lift will not operate", "myki machines will
    // not operate" and "some services will not run on Sunday" are not a missing
    // train. Nor is a train that skips something ("do not run express", "do not
    // run past the stop").
    String.raw`(?:trains?${ROAD_WORDS ? `|${ROAD_WORDS}` : ''})\s+(?:(?:do|does|will|are|is)\s+)?not\s+(?:run|operate)\b(?!\s+(?:express|past|through|via)\b)`,
    String.raw`no\s+(?:trains${ROAD_WORDS ? `|${ROAD_WORDS}` : ''})`,
    'not running',
    // The line is closed, and Translink's "track closure".
    // Only where the thing closed is the LINE: "Moorooka station temporarily
    // closed" is a station the trains run through, and "temporarily closed" on
    // its own is therefore not a rule.
    String.raw`(?:line|railway|track|tracks)\s+(?:(?:is|will\s+be|has\s+been|remains)\s+)?(?:temporarily\s+)?closed`,
    // "Weekday track closure" (Translink), "full Outer Harbor rail line closure"
    // (Adelaide). NOT a bare "line closure": "Glenelg partial line closure" is a
    // tram line whose trams keep running to the far stop.
    String.raw`(?:track|rail\s+line)\s+closures?`,
  ].join('|')
  + String.raw`)\b`,
  'i',
);

/**
 * What is standing in for the trains, if anything.
 *
 * The same sentence `NOT_RUNNING` already matched. V/Line says coaches,
 * Metro says buses, and a line that is simply closed names neither. The
 * ledger word and the push title both ask this, because SUSPENDED over a
 * coach replacement is the sentence that sends somebody home — or onto a
 * platform for a train that is a coach.
 */
export function replacementOf(text) {
  const s = String(text ?? '');
  if (!NOT_RUNNING.test(s)) return null;
  if (/\bcoach(?:es)?\b/i.test(s)) return 'coaches';
  if (/\b(?:bus(?:es)?|railbus(?:es)?|minibus(?:es)?)\b/i.test(s)) return 'buses';
  return null;
}

/** ONE cancelled run, not a line: "The 9:06am Craigieburn to Flinders Street service has been cancelled." */
const SERVICE_CANCELLED = /\bservice (has been |is )?cancelled\b/i;

/** The clock PTV puts on a single cancelled run: "The 5:50pm Arden to Dandenong service". */
const SERVICE_CLOCK = /\b(\d{1,2}):(\d{2})\s*(am|pm)\b/i;

/** How long after the named departure a one-run cancellation is still the news. */
const CANCELLATION_GRACE_MS = 2 * 60 * 60_000;

/**
 * When the named service was due, or null when the title has no clock.
 *
 * The date is the notice's `startedAt` day. If that clock is already behind
 * the announcement (Sunday night saying Monday's 5:50pm is cancelled), it
 * rolls forward one day.
 */
export function cancelledServiceAt(d) {
  const text = `${d?.title ?? ''} ${d?.description ?? ''}`;
  const m = SERVICE_CLOCK.exec(text);
  if (!m) return null;
  const started = Date.parse(d?.startedAt ?? d?.publishedAt ?? '');
  if (!Number.isFinite(started)) return null;
  let hour = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) hour += 12;
  const at = new Date(started);
  at.setHours(hour, Number(m[2]), 0, 0);
  if (at.getTime() < started - 2 * 60_000) at.setDate(at.getDate() + 1);
  return at.getTime();
}

/** A 5:50pm cancellation is not Tuesday's headline. */
export function cancellationStillCurrent(d, now = Date.now()) {
  const at = cancelledServiceAt(d);
  if (at == null) return true;
  return now < at + CANCELLATION_GRACE_MS;
}

/**
 * ONE RUN CUT SHORT — V/Line's commonest disruption, and it is not worded as a
 * cancellation anywhere in the payload.
 *
 * "The 2:36pm Southern Cross to Ararat service will end early at Ballarat and
 * no longer run to Ararat due to an earlier delay." "The 5:17pm Ararat to
 * Southern Cross service will originate from Ballarat at 6:14pm and not
 * Ararat." PTV types both "Service Information", which is not a severity at
 * all, and the prose contains no delay figure and no "cancelled" — so both
 * classified as notices, and a person waiting at Ararat for the only train in
 * three hours was told nothing.
 *
 * It is read as a `cancellation` rather than as a suspension for the reason
 * that word already carries here: this is ONE service, not a closed line. On a
 * line with four trains a day it happens to be most of the service, which is
 * precisely why it earns a push.
 *
 * Narrow on purpose — it wants the word "service" and one of two verbs, so
 * "buses will terminate early" and "works will end early on Sunday" are not it.
 */
const SERVICE_CURTAILED = /\bservice will (end early|terminate early|originate from)\b/i;

/** "The 2:36pm Southern Cross to Ararat service" — clock, origin, destination. */
const SERVICE_NAMED = /\b(\d{1,2}:\d{2}\s*(?:am|pm))\s+(.+?)\s+to\s+(.+?)\s+service\b/i;

const ENDS_AT = /will (?:end|terminate) early at ([A-Za-z][A-Za-z .'-]+?)(?:\s+and|\s+due|\.|$)/i;

const STARTS_AT = /will originate from ([A-Za-z][A-Za-z .'-]+?)(?:\s+at|\s+and|\s+due|\.|$)/i;

/** The place a curtailment names, without PTV's trailing clause. */
function placeName(raw) {
  return String(raw ?? '')
    .replace(/\s+Railway Station$/i, '')
    .replace(/\s+(and|due|at)\s*$/i, '')
    .trim();
}

/**
 * The one line the ledger can hold about a cancelled or curtailed service.
 *
 * Generic "Service cancelled" is true and useless: the pain is turning up
 * for the 2:36pm to Ararat and finding it ends at Ballarat. The clock and
 * the place are already in the title; this only lifts them out.
 */
export function cancellationNote(d) {
  const text = `${d?.title ?? ''} ${d?.description ?? ''}`;
  const named = SERVICE_NAMED.exec(text);
  const clock = (named?.[1] ?? SERVICE_CLOCK.exec(text)?.[0] ?? '').replace(/\s+/g, '');
  const origin = placeName(named?.[2] ?? '');
  const ends = placeName(ENDS_AT.exec(text)?.[1] ?? '');
  const starts = placeName(STARTS_AT.exec(text)?.[1] ?? '');
  if (clock && ends) return `${clock} ends at ${ends}`;
  if (clock && starts) return `${clock} from ${starts}`;
  if (clock && origin) return `${clock} ${origin} cancelled`;
  if (clock) return `${clock} cancelled`;
  return 'Service cancelled';
}

/** Station furniture. A car park closure or a lift outage is not the railway. */
const FACILITY = /\b(car ?park|lift|escalator|pedestrian|access changes|toilets?|bike|parking|station (works|upgrade|forecourt))\b/i;

/**
 * A planned notice earns a ledger row only when it is about the service running.
 *
 * `lines?` RATHER THAN `line`, which it was until Brisbane arrived. PTV writes
 * "the line is closed" and Translink writes "Affected lines: Beenleigh and Gold
 * Coast" — and `line` with a word boundary after it does not match "lines", so
 * every Queensland Rail track closure fell through this guard and landed as a
 * notice on a detail screen nobody was going to open. One letter, and the
 * plural is as much about the service running as the singular.
 */
const ABOUT_SERVICE = /\b(trains?|buses?|services?|lines?)\b/i;

/**
 * How far ahead a planned works block earns a ledger row.
 *
 * PTV publishes October's night works in September, and eleven of seventeen
 * lines carrying PLANNED WORKS for something three weeks away is the same
 * failure as everything being DELAYED, one tier down: a ledger that always has
 * writing on it is a ledger nobody scans. Two days is the horizon at which a
 * works block changes what someone does tomorrow morning. Outside it the row is
 * still there, as a notice on the line's detail screen.
 */
export const PLANNED_HORIZON_MS = 48 * 60 * 60 * 1000;

/** Is this planned window in force, or close enough to matter? */
function withinHorizon(d, now) {
  const ends = Date.parse(d?.endsAt ?? '');
  if (Number.isFinite(ends) && ends < now) return false;
  const starts = Date.parse(d?.startedAt ?? '');
  // No dates at all: PTV is saying this is in force, so believe it.
  if (!Number.isFinite(starts)) return true;
  return starts <= now + PLANNED_HORIZON_MS;
}

/**
 * One of 'suspended' | 'delayed' | 'cancellation' | 'planned' | 'notice'.
 *
 * Pure: it reads only the row it is given and the clock it is handed, so it can
 * be tested against titles copied verbatim out of the live feed.
 */
export function severityOf(d, now = Date.now()) {
  // A street-running mode (a tram) is not a train with a different badge: a
  // two-minute hold-up behind a turning car is normal running, and a diversion
  // is a thing trains never do. Its own rules, below, in the same vocabulary.
  const mode = modeOfRow(d);
  if (mode?.severity.rules === 'street') return streetSeverityOf(d, now, mode);

  const type = String(d?.type ?? '');
  const status = String(d?.status ?? '');
  const current = status === 'current';
  const text = `${d?.title ?? ''} ${d?.description ?? ''}`;

  // A works block that runs 8.30pm to last service every night for three weeks
  // arrives with `disruption_status: "Current"` from the day it is announced,
  // because its from_date has passed. The TYPE is what says it is planned, and
  // believing the status instead is why Werribee and Williamstown read
  // SUSPENDED at 9:37 on a Wednesday morning with trains running normally.
  const plannedRow = status === 'planned' || /^planned/i.test(type);

  // Suspended. PTV says so outright ("Suspended", "Part Suspended"), or the
  // prose does — EXCEPT where PTV has already typed the row as a delay (trains
  // are running; buses are covering one section) or as planned works.
  if (/suspend/i.test(type)) return 'suspended';
  // A STATION DETOUR IS ABOUT A BUILDING. "Northgate station - temporary railbus
  // stop change: if you normally catch your rail replacement service from here,
  // use the stop on Melton Road" names a replacement service and is not one.
  if (current && !DELAY_TYPES.has(type) && !plannedRow && type !== 'Station detour'
    && NOT_RUNNING.test(text)) return 'suspended';

  // Delayed. The type, or a figure in the prose of something happening now.
  if (DELAY_TYPES.has(type)) return 'delayed';
  if (current && delayMinutesFrom(text) !== null) return 'delayed';

  // A cancelled service is one train, not a closed line. It matters — it is why
  // the platform is full — but it is not the most severe thing that can happen.
  // And it is only the news until that train's minute has gone: yesterday's
  // 5:50pm cancellation must not outrank today's delays on the same line.
  const oneService = type === 'Cancellation'
    || (current && SERVICE_CANCELLED.test(text))
    || (current && !plannedRow && SERVICE_CURTAILED.test(text));
  if (oneService) return cancellationStillCurrent(d, now) ? 'cancellation' : 'notice';

  // Planned, and about the trains. "Buses replace trains from 8.30pm Friday" is
  // a planned works row; "temporary car park closures" is not.
  if (plannedRow && PLANNED_TYPES.has(type) && !FACILITY.test(text) && ABOUT_SERVICE.test(text)) {
    return withinHorizon(d, now) ? 'planned' : 'notice';
  }

  return 'notice';
}

// Trouble first, and a cancelled service below a delay because a delay is the
// thing that changes what you do next.
//
// TWO RANKS ARE TRAMS' ONLY. `diverted` is a tram that will not reach some of
// its stops, which is nearly a suspension for anybody standing at one of them.
// `slow` is a tram running a few minutes late: shown, and never a reason to
// wake anybody — see `streetSeverityOf`. No train row is ever either, so the
// order of everything a train can be is unchanged.
export const SEVERITY_RANK = {
  suspended: 0, diverted: 1, delayed: 2, slow: 3, cancellation: 4, planned: 5, notice: 6,
};

/**
 * The severities worth interrupting someone for. Notices and planned works are
 * not — a phone that buzzes for a lift outage is a phone with alerts turned off
 * by the end of the week.
 *
 * THE SAME SET GOVERNS BOTH SENDERS. The app uses it to decide whether to
 * schedule a local notification; `server/push.mjs` uses it to decide whether a
 * disruption is worth a push. One set, so the two can never disagree.
 */
export const ALERTABLE = new Set(['suspended', 'diverted', 'delayed', 'cancellation']);

/** Is this severity worth waking someone for? */
export function isAlertable(severity) {
  return ALERTABLE.has(severity);
}

/**
 * Has it got worse? Lower rank is worse, so a rise in severity is a FALL in
 * rank. Unknown severities sort last, which makes them "not worse".
 */
export function hasWorsened(before, after) {
  const was = SEVERITY_RANK[before] ?? Number.MAX_SAFE_INTEGER;
  const now = SEVERITY_RANK[after] ?? Number.MAX_SAFE_INTEGER;
  return now < was;
}

/** Rows worst first, then newest. */
export function sortBySeverity(rows, now = Date.now()) {
  return [...rows].sort((a, b) => SEVERITY_RANK[severityOf(a, now)] - SEVERITY_RANK[severityOf(b, now)]
    || String(b.startedAt).localeCompare(String(a.startedAt)));
}

/**
 * One line's status, from rows already sorted worst-first.
 *
 * The rows arrive worst-first, so the answer is the first one — which is how
 * "Delays up to 15 minutes due to a train fault in the Craigieburn area" beats
 * three newer station notices and the row finally reads `+15 MIN`.
 */
export function statusFromRows(rows, now = Date.now()) {
  if (!rows.length) {
    return { status: 'good', delayMinutes: null, note: 'Running to timetable', top: null, replacement: null };
  }

  const top = rows[0];
  const text = `${top.title} ${top.description ?? ''}`;

  switch (severityOf(top, now)) {
    case 'suspended':
      return {
        status: 'suspended',
        delayMinutes: null,
        note: top.title,
        top,
        replacement: replacementOf(text),
      };

    case 'delayed': {
      // The figure if the notice states one, the word if it does not. The row is
      // built for both and does not reflow between them.
      const mins = Number.isFinite(top.delayMinutes) ? top.delayMinutes : delayMinutesFrom(text);
      return { status: 'delayed', delayMinutes: mins, note: top.title, top, replacement: null };
    }

    case 'cancellation': {
      // The ledger word stays DELAYED. A cancelled service is felt as a delay,
      // and a fifth status word would be a fifth thing to learn for no gain —
      // but the note names the clock, and how many, so somebody waiting for
      // the 2:36pm does not read a generic cancellation and stay put.
      const cancelled = rows.filter((d) => severityOf(d, now) === 'cancellation');
      const first = cancellationNote(cancelled[0] ?? top);
      const note = cancelled.length > 1 ? `${cancelled.length} services cancelled · ${first}` : first;
      return { status: 'delayed', delayMinutes: null, note, top, replacement: null };
    }

    // A diverted tram ranks with a suspension on the ledger — for anybody at a
    // stop it skips, it IS one — and changes only the word, the way buses do.
    case 'diverted':
      return {
        status: 'suspended', delayMinutes: null, note: top.title, top, replacement: 'diverted',
      };

    // A few minutes late. The same row as a delay, figure and all: it is true
    // and it is useful. What differs is upstream — `slow` is not ALERTABLE.
    case 'slow': {
      const mins = Number.isFinite(top.delayMinutes) ? top.delayMinutes : delayMinutesFrom(text);
      return { status: 'delayed', delayMinutes: mins, note: top.title, top, replacement: null };
    }

    case 'planned':
      return { status: 'planned', delayMinutes: null, note: top.title, top, replacement: null };

    default:
      // Notices only: the line is running. They are counted rather than hidden
      // so the detail screen can still list the station works nobody needs to
      // be woken for.
      return {
        status: 'good', delayMinutes: null, note: 'Running to timetable',
        notices: rows.length, top, replacement: null,
      };
  }
}

/**
 * The headline a notification uses for a severity.
 *
 * "Delays" rather than "Major delays": `severityOf` folds PTV's Minor Delays
 * and Major Delays into one severity, and a push that says MAJOR for a
 * four-minute notice is the kind of small lie that costs the whole feature its
 * credibility. The disruption's own title is the body, and it carries the
 * degree in PTV's own words.
 */
/** "Trams diverted": the first street-running mode's plural, or plain "Diverted" with none. */
function divertedLabel(word = null) {
  const first = word ?? vehicleModes()[0]?.labels.many;
  return first ? `${capital(first)} diverted` : 'Diverted';
}

export const SEVERITY_LABEL = {
  suspended: 'Trains suspended',
  diverted: divertedLabel(),
  delayed: 'Delays',
  slow: 'Delays',
  cancellation: 'Service cancelled',
  planned: 'Planned works',
  notice: 'Notice',
};

/**
 * The plural vehicle word of a street-running mode the text names, when it
 * names one and no train: "trams" from "Buses replace trams". Null otherwise.
 */
function streetWordIn(text, mode = null) {
  if (/\btrains?\b/i.test(text)) return null;
  // A row that knows its mode names it when the scan below cannot: a bus
  // notice rarely says "bus" ("Route 506 services are diverting").
  if (mode?.severity.rules === 'street' && mode.severity.replaceable === false) return mode.labels.many;
  // By the text alone, only the modes a replacement bus stands in for: a train
  // notice that happens to say "buses" is not a bus notice.
  for (const m of vehicleModes()) {
    if (m.severity.replaceable === false) continue;
    if (new RegExp(`\\b(?:${m.labels.one}|${m.labels.many})\\b`, 'i').test(text)) return m.labels.many;
  }
  return null;
}

/**
 * The headline a notification uses, with the vehicle named when there is one.
 *
 * `SEVERITY_LABEL` is the fallback. A V/Line coach replacement that says
 * "Trains suspended" is the same lie the ledger used to print: the trains
 * are not coming, and the coaches are. `mode` is the adapter mode the row is
 * about, when the caller knows it: a bus notice is labelled "Buses diverted"
 * whether or not its prose says "bus".
 */
export function labelFor(severity, text = '', mode = null) {
  if (severity === 'suspended') {
    // A street-mode notice says its vehicle, and the label must too. Asked of
    // the words rather than the line, because this is handed the text alone.
    const word = streetWordIn(text, mode);
    const kind = replacementOf(text);
    if (kind === 'coaches') return 'Coaches replace trains';
    // "Buses replace buses" is not a sentence: a mode a replacement bus cannot
    // stand in for is simply suspended.
    if (kind === 'buses' && mode?.severity.replaceable !== false) return word ? `Buses replace ${word}` : 'Buses replace trains';
    if (word) return `${capital(word)} suspended`;
  }
  if (severity === 'diverted') {
    const word = streetWordIn(text, mode);
    if (word) return divertedLabel(word);
  }
  return SEVERITY_LABEL[severity] ?? 'Disruption';
}

/**
 * The word the ledger and the line stamp print.
 *
 * Coaches and buses keep the suspended rank — they are still the worst
 * thing on the line — and change only the word, so a scan that used to
 * read SUSPENDED and stop now reads COACHES and knows there is a way.
 */
export function statusWord(state, { live = true } = {}) {
  if (state?.status === 'suspended') {
    if (state.replacement === 'diverted') return 'Diverted';
    if (state.replacement === 'coaches') return 'Coaches';
    if (state.replacement === 'buses') return 'Buses';
    return 'Suspended';
  }
  if (state?.status === 'delayed') return 'Delayed';
  if (state?.status === 'planned') return 'Planned works';
  if (state?.status === 'good') {
    // A city with nothing live has no "good service" to report: the row only
    // means that nothing is known to be wrong, and nothing is being sent.
    return live === false ? 'Timetable' : 'Good service';
  }
  return '';
}

/**
 * The notice a line should lead with: current trouble, worst first.
 *
 * A sent cancellation from Monday is still a row in the inbox. The heading
 * somebody opens must be what is happening now — today's delay, not the
 * 5:50pm that already ran.
 */
export function leadDisruption(rows, now = Date.now()) {
  const live = sortBySeverity(
    (rows ?? []).filter((d) => d && !d.clearedAt),
    now,
  );
  return live.find((d) => ALERTABLE.has(severityOf(d, now))) ?? null;
}

/**
 * When the notice last moved, for a ledger that leads with the current story.
 *
 * `sentAt` is when a push went out, and a line can have a Monday send sitting
 * in front of a delay that PTV revised this afternoon. The heading is the
 * delay; the day has to be the delay's too — `updatedAt` first, then when it
 * started, then when it was published, and only then the push.
 */
export function whenOf(d) {
  let latest = NaN;
  for (const key of ['updatedAt', 'startedAt', 'publishedAt', 'sentAt']) {
    const t = Date.parse(d?.[key] ?? '');
    if (Number.isFinite(t) && (Number.isNaN(latest) || t > latest)) latest = t;
  }
  return Number.isFinite(latest) ? latest : null;
}

/**
 * TODAY / YESTERDAY / a dated label, in the phone's calendar.
 *
 * Yesterday is the previous calendar day, not twenty-four hours ago: a 5pm
 * send looked at at 4pm the next afternoon is still yesterday, and subtracting
 * 86_400_000 around a DST fold is how a Tuesday morning became TODAY.
 */
export function dayHeading(at, now = Date.now()) {
  const d = at instanceof Date ? at : new Date(at);
  const today = new Date(now);
  if (Number.isNaN(d.getTime()) || Number.isNaN(today.getTime())) return '';
  const yday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'TODAY';
  if (d.toDateString() === yday.toDateString()) return 'YESTERDAY';
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
}

// ── Street-running modes ──────────────────────────────────────────────────
//
// A mode that shares the road (`severity.rules: 'street'` in src/modes.js) is
// judged on its own terms. For Melbourne's trams they are the owner's: A TRAM
// ALERT IS A DIVERSION, A BUS REPLACEMENT, OR A DELAY OVER TEN MINUTES, the ten
// being the mode's `alertMinutes`. A tram shares the road; two minutes behind a
// turning truck is not news, and a phone that buzzed for every one would be a
// phone with tram alerts turned off by Friday.
//
// The answer is in the SAME VOCABULARY as a train's, so the ledger, the push
// sweep and the app's own alerts need no second idea of what severity is:
//
//   suspended  no tram between two points, or buses instead      alerts
//   diverted   running, but not past some of its stops            alerts
//   delayed    late by the mode's alertMinutes or more                 alerts
//   slow       late by less, or late by an amount nobody stated   shown, quiet
//   planned    works or changes inside the 48-hour horizon        shown, quiet
//   notice     everything else — a closed stop, a far-off event   detail only
//
// `slow` is the one new idea. It is not in ALERTABLE, so every sender skips it
// for the same reason every sender skips a planned works block: the predicate
// lives in one place.

/** "Route 96 trams are diverting via Spencer St", "will divert", "diversion". */
const STREET_DIVERTED = /\bdivert(s|ed|ing)?\b|\bdiversions?\b|\bwill run via\b|\bare running via\b/i;

/**
 * Trams that stop short, or cannot run a stretch: real service loss that is
 * not worded "diverting". Partial suspension, so diversion-class.
 */
const STREET_PARTIAL = /\b(?:terminat(?:e|es|ed|ing)|short[- ]?turn(?:s|ed|ing)?|unable to run)\b/i;

/**
 * Stops the vehicle does not call at: "will not service the following stops",
 * "will skip", "not stopping at". The route runs, past some of its stops, which
 * is what a diversion is to somebody waiting at one of them. Whatever else the
 * notice says (PTV may call it "Major Delays"), this is the worse news.
 */
const STREET_SKIPPED = /\b(?:will not|won'?t|do not|does not|not) (?:service|serve|stop at|stopping at|call at|stop)\b(?:\s+\w+){0,4}\s+stops?\b|\bnot (?:servic|serv)ing\b(?:\s+\w+){0,4}\s+stops?\b|\b(?:skip|skips|skipping|bypass(?:es|ing)?)\b(?:\s+\w+){0,4}\s+stops?\b|\bwill not (?:service|stop at)\b/i;

/** "Services suspended until further notice" — suspended, said in prose. */
const STREET_SUSPENDED = /\b(?:services?|trams?|routes?)\b(?:\s+\w+){0,3}\s+suspended\b|\bsuspended\s+until\b/i;

/**
 * The notice is saying it is OVER, or what the destination blind will say:
 * "Normal service has resumed. Trams no longer terminating at Flinders St",
 * "Services resumed after a suspended service earlier", "Terminating trams at
 * Melbourne University will show Route 1". The words that name service loss are
 * in them, and they are good news or information, so they are read before the
 * loss words are. Future tense ("normal service will resume at 5pm") is not
 * resolution: the loss is still on.
 */
const STREET_RESOLVED = /\bno longer\b|\bresumed\b|\bwill show\b|\bnormal service (?:has|have|is now)\b/i;

/**
 * A stop closed, not the route. "Temporary stop closure on Kings Way until
 * further notice" has been Route 58's only notice since January; reading it
 * as works on the route would print PLANNED WORKS beside 58 for a year.
 */
const STREET_STOP_ONLY = /\bstop closures?\b|\bstops? (is|are|will be) (temporarily )?closed\b/i;

/** Changes to the service at all, for a planned notice. */
const STREET_ABOUT_SERVICE = new RegExp(
  `\\b(${[...vehicleWords(), 'routes?', 'services?', 'service changes', 'timetable'].join('|')})\\b`,
  'i',
);

/** Is this row about a street-running mode? Re-exported for readers of this file. */
export const isStreetRow = (d) => modeOfRow(d)?.severity.rules === 'street';

/**
 * One street-mode notice's severity. Pure, like `severityOf`, which hands it
 * every such row: the type first where PTV's type is decisive, the prose for
 * the rest. `mode` carries the delay threshold; without one, the first street
 * mode's.
 */
export function streetSeverityOf(d, now = Date.now(), mode = modeOfRow(d)) {
  const alertMinutes = mode?.severity.alertMinutes ?? vehicleModes()[0]?.severity.alertMinutes ?? 10;
  const type = String(d?.type ?? '');
  const status = String(d?.status ?? '');
  const text = `${d?.title ?? ''} ${d?.description ?? ''}`;
  const plannedRow = status === 'planned' || /^planned/i.test(type);
  const current = status === 'current' && !plannedRow;

  const resolved = STREET_RESOLVED.test(text);
  if (current && (NOT_RUNNING.test(text) || /suspend/i.test(type) || (!resolved && STREET_SUSPENDED.test(text)))) return 'suspended';
  if (current && (type === 'Diversion' || STREET_DIVERTED.test(text) || (!resolved && (STREET_PARTIAL.test(text) || STREET_SKIPPED.test(text))))) return 'diverted';

  const minutes = Number.isFinite(d?.delayMinutes) ? d.delayMinutes : delayMinutesFrom(text, { hours: true });
  if (current && (DELAY_TYPES.has(type) || minutes !== null || /\bdelays?\b/i.test(text))) {
    if (minutes !== null) return minutes >= alertMinutes ? 'delayed' : 'slow';
    // No figure. PTV's own "Major Delays" is the one word that means more
    // than a few minutes; anything vaguer — "delays may occur as we restore
    // the timetable" — is shown and kept quiet.
    return type === 'Major Delays' ? 'delayed' : 'slow';
  }

  if (STREET_STOP_ONLY.test(text) && !STREET_DIVERTED.test(text)) return 'notice';

  // Planned, or PTV's two words for a change to how the route runs, and about
  // the service rather than a shelter: inside the 48-hour horizon it is on the
  // ledger, past it it is on the detail screen. The same horizon as a train.
  if ((plannedRow || /route changes|service information/i.test(type))
    && STREET_ABOUT_SERVICE.test(text) && !FACILITY.test(text)) {
    return withinHorizon(d, now) ? 'planned' : 'notice';
  }
  return 'notice';
}
